/**
 * SchedulerDashboardMobile.jsx
 * Mobile-optimized scheduler interface with full-height viewport, touch-friendly controls,
 * and mobile-specific UI patterns. Includes exercise scheduling, push notifications, and
 * calendar integration (Add to Calendar button).
 *
 * Mobile-specific features:
 *  - Full-height container (100vh) with viewport management
 *  - Touch-friendly icon buttons (TbCalendarPlus, TbCalendarShare)
 *  - Mobile FullCalendar view (listWeek instead of listMonth)
 *  - Bottom notification banner for push permission requests
 *  - Safari push notification handling (PushAlert integration)
 *  - Welcome greeting with user name
 *  - Responsive dialog for exercise entry
 *  - Add to Calendar integration (atcb_action)
 *  - Schedule edit/delete with repeat interval management
 *
 * Uses modern patterns:
 *  - useScheduler hook for CRUD operations and caching
 *  - useFirebaseNotifications for push notification setup
 *  - Zustand for global state
 *  - TanStack Query for server state (via useScheduler)
 *  - i18next for multi-language support
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Button,
  Snackbar,
  Typography,
  Container,
  CircularProgress,
  Box,
} from "@mui/material";
import { Toast } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import FullCalendar from "@fullcalendar/react";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { useTranslation } from "react-i18next";
import { TbCalendarShare, TbCalendarPlus } from "react-icons/tb";
import { atcb_action } from "add-to-calendar-button";
import { useQueryClient } from "@tanstack/react-query";

import { useScheduler } from "../hooks/useScheduler";
import { useFirebaseNotifications } from "../hooks/useFirebaseNotifications";
import NotificationDialog from "../components/scheduler/NotificationDialog";

import { useTourStore } from "../stores/zustand";
import { useAuthStore } from "../stores/zustand";

/**
 * SchedulerDashboardMobile component (memoized)
 * Mobile-optimized scheduler with full-height viewport and touch controls.
 */
const SchedulerDashboardMobile = React.memo(
  ({ isFirstLogin = false, wasAutoLogin = false, wasStartup = false }) => {
    const { t } = useTranslation();
    const { startTour } = useTourStore();

    const queryClient = useQueryClient();
    const email = useAuthStore((state) => state.user.email);
    const currentLanguage = useAuthStore((state) => state.preferredLanguage);

    const getTimezoneOffset = () => {
      const offset = new Date().getTimezoneOffset() / -60;
      return offset >= 0
        ? `+${String(Math.abs(offset)).padStart(2, "0")}`
        : `-${String(Math.abs(offset)).padStart(2, "0")}`;
    };
    const timezoneOffset = getTimezoneOffset();

    // Refs for calendar container and tracking
    const calendarRef = useRef(null);
    const calendarContainerRef = useRef(null);

    // Local UI State
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [safariPushAlertToken, setSafariPushAlertToken] = useState(null);
    const [editingData, setEditingData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // User state
    const [userName, setUserName] = useState("");

    // Initialize Firebase notifications hook
    const firebaseNotifications = useFirebaseNotifications();

    // Initialize scheduler hook (manages all CRUD operations and caching)
    const scheduler = useScheduler();

    // Destructure scheduler state and actions
    const {
      exerciseTypes,
      error: schedulerError,
      calendarEvents,
      schedules,
      createNotification,
      editNotification,
      editSchedule,
      deleteSchedule,
      deleteNotification,
      refreshData,
      schedulerIsFetching,
      homeIsLoading,
      getCalendarToken,
    } = scheduler;

    const currentProduct = useAuthStore((state) => state.currentProduct);
    const isPhysioUser = currentProduct === "physio_user";

    // Destructure Firebase notification utilities
    const {
      // showNotificationBanner,
      // handleEnablePushNotifications,
      // handleOpenNotificationGuide,
      registerSafariToken,
      // isPermissionDenied,
    } = firebaseNotifications;

    // Extract user name from cache
    useEffect(() => {
      const currentHomeData = queryClient.getQueryData([
        "homeData",
        email,
        timezoneOffset,
        currentLanguage,
      ]);

      if (
        currentHomeData?.rawHomeData?.name ||
        currentHomeData?.rawHomeData?.fullName
      ) {
        setUserName(
          currentHomeData.rawHomeData.fullName ||
            currentHomeData.rawHomeData.name
        );
      }
    }, [queryClient, email, timezoneOffset, currentLanguage]);

    // Handle first-login setup (start guided tour)
    useEffect(() => {
      if (isFirstLogin) {
        startTour();
      }
    }, [isFirstLogin, startTour]);

    // Observe calendar container resize for responsive height management
    useEffect(() => {
      // Calendar height tracking can be added back if needed
      // Currently using auto height for responsive design
      if (calendarContainerRef.current) {
        // Just ensure the container is mounted
      }
    }, []);

    // Safari-specific push notification registration (PushAlert)
    useEffect(() => {
      function onPAReady() {
        const subscriberID = window.PushAlertCo?.subs_id;
        if (subscriberID) {
          setSafariPushAlertToken(subscriberID);
        }
      }

      window.pushalertbyiw = window.pushalertbyiw || [];
      window.pushalertbyiw.push(["onReady", onPAReady]);
    }, []);

    // Register Safari push token when available
    useEffect(() => {
      if (safariPushAlertToken) {
        registerSafariToken(safariPushAlertToken);
      }
    }, [safariPushAlertToken, registerSafariToken]);

    // Watch temporary event becoming real while dialog is open
    // When a temp event (optimistic update) is confirmed by server,
    // update dialog data to reflect the real event ID
    useEffect(() => {
      if (isDialogOpen && editingData && isEditing) {
        const currentScheduleId = editingData.scheduleId;
        const isTemp = String(currentScheduleId).startsWith("temp-");

        if (isTemp) {
          // Check if this temp event has become real by looking in cache
          const currentHomeData = queryClient.getQueryData([
            "homeData",
            email,
            timezoneOffset,
            currentLanguage,
          ]);

          const tempSchedule = currentHomeData?.schedules.find(
            (s) => s.id === currentScheduleId
          );

          if (!tempSchedule) {
            // Temp event no longer in cache - it became real
            // Find the real event by matching time and preferenceId
            const tempDate = editingData.date;
            const tempTime = editingData.time;
            const tempPreferenceId = editingData.exerciseTypes?.[0];

            const matchingSchedule = schedules.find((s) => {
              const localDate = new Date(s.notificationTime);
              // Proper date extraction for comparison
              const year = localDate.getFullYear();
              const month = String(localDate.getMonth() + 1).padStart(2, "0");
              const day = String(localDate.getDate()).padStart(2, "0");
              const sDate = `${year}-${month}-${day}`;
              const sTime = localDate.toTimeString().slice(0, 5);

              return (
                sDate === tempDate &&
                sTime === tempTime &&
                s.preferenceId === tempPreferenceId
              );
            });

            if (matchingSchedule) {
              // Update editingData with real schedule
              const localDate = new Date(matchingSchedule.notificationTime);
              // Proper date extraction
              const year = localDate.getFullYear();
              const month = String(localDate.getMonth() + 1).padStart(2, "0");
              const day = String(localDate.getDate()).padStart(2, "0");
              const date = `${year}-${month}-${day}`;
              const time = localDate.toTimeString().slice(0, 5);

              const updatedDialogData = {
                date: date || "",
                time: time || "",
                exerciseTypes: [matchingSchedule.preferenceId],
                repeatInterval: matchingSchedule.repeatInterval || "NONE",
                repeatPeriod:
                  matchingSchedule.repeatPeriod?.split("T")[0] || "",
                scheduleId: matchingSchedule.id,
                notificationId: matchingSchedule.notificationId,
                type: matchingSchedule.type,
                video: matchingSchedule.video,
                name: matchingSchedule.name,
                isScheduleEdit: true,
              };

              setEditingData(updatedDialogData);
            }
          }
        }
      }
    }, [
      isDialogOpen,
      editingData,
      isEditing,
      schedules,
      queryClient,
      email,
      timezoneOffset,
      currentLanguage,
    ]);

    // Show temporary snackbar notification (defined before Firebase listener to avoid reference issues)
    const displaySnackbarMessage = useCallback((message) => {
      setSnackbarMessage(message);
      setSnackbarOpen(true);
      setTimeout(() => {
        setSnackbarMessage("");
        setSnackbarOpen(false);
      }, 3000);
    }, []); // No dependencies - stable function reference

    // Get timezone offset string
    // Tour handlers (kept for consistency with original, can be used if needed)
    // const handleStartTour = useCallback(() => {
    //   setIsTourOpen(true);
    //   startTour();
    // }, [startTour]);

    // const handleCloseTour = useCallback(() => {
    //   setIsTourOpen(false);
    // }, []);

    // const handleStartBigTour = useCallback(() => {
    //   setIsIntroductionDialogOpen(false);
    //   handleStartTour();
    // }, [handleStartTour]);

    // Dialog handlers
    const handleDialogOpen = useCallback(() => {
      const currentTime = new Date().toTimeString().slice(0, 5);

      const defaultData = {
        date: "",
        time: currentTime,
        exerciseTypes: exerciseTypes.length > 0 ? [exerciseTypes[0].id] : [],
        repeatInterval: "NONE",
        repeatPeriod: "",
        name: "",
        video: null,
        type: null,
        isScheduleEdit: false,
      };

      setEditingData(defaultData);
      setIsEditing(false);
      setIsDialogOpen(true);
    }, [exerciseTypes]);

    const handleDialogClose = useCallback(() => {
      setIsDialogOpen(false);
      setTimeout(() => {
        setEditingData(null);
        setIsEditing(false);
      }, 300);
    }, []);

    // Handle form submission (create or update)
    const handleFormSubmit = useCallback(
      async (formData) => {
        try {
          // Handle schedule edit (entire series)
          if (isEditing && editingData?.isScheduleEdit) {
            editSchedule({
              scheduleId: editingData.scheduleId,
              notificationData: formData,
            });
          }
          // Handle single notification edit
          else if (isEditing) {
            editNotification({
              notificationId: editingData.notificationId,
              notificationData: formData,
            });
          }
          // Create new notification
          else {
            createNotification(formData);
          }

          displaySnackbarMessage(
            isEditing ? t("notificationUpdated") : t("notificationCreated")
          );
          handleDialogClose();
        } catch (error) {
          console.error("Error submitting form:", error);
          displaySnackbarMessage(t("unexpectedError"));
        }
      },
      [
        isEditing,
        editingData,
        editSchedule,
        editNotification,
        createNotification,
        handleDialogClose,
        displaySnackbarMessage,
        t,
      ]
    );

    // Handle delete entry (single notification or schedule)
    const handleDeleteSchedule = useCallback(
      async (scheduleId, deleteType) => {
        try {
          // Delete entire series or single instance
          let result = false;
          if (deleteType === "series") {
            result = await deleteNotification(scheduleId);
          } else {
            result = await deleteSchedule(scheduleId);
          }

          displaySnackbarMessage(
            result ? t("notificationDeleted") : t("failedToDeleteNotification")
          );

          if (result) {
            handleDialogClose();
          }
        } catch (error) {
          console.error("Delete error:", error);
          displaySnackbarMessage(t("unexpectedError"));
        }
      },
      [
        deleteSchedule,
        deleteNotification,
        handleDialogClose,
        displaySnackbarMessage,
        t,
      ]
    );

    // Handle calendar event click for editing
    const handleEventClick = useCallback(
      (info) => {
        const eventId = info.event.id;

        // Check if this is a temporary event (optimistic update)
        const isTemp = String(eventId).startsWith("temp-");

        if (isTemp) {
          // Load from cache instead of optimistic map
          const currentHomeData = queryClient.getQueryData([
            "homeData",
            email,
            timezoneOffset,
            currentLanguage,
          ]);

          const schedule = currentHomeData?.schedules.find(
            (s) => s.id === eventId
          );

          if (schedule) {
            // Convert notificationTime to local time with proper date extraction
            const localDate = new Date(schedule.notificationTime);
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, "0");
            const day = String(localDate.getDate()).padStart(2, "0");
            const date = `${year}-${month}-${day}`;
            const time = localDate.toTimeString().slice(0, 5);

            const dialogData = {
              date: date || "",
              time: time || "",
              exerciseTypes: [schedule.preferenceId],
              repeatInterval: schedule.repeatInterval || "NONE",
              repeatPeriod: schedule.repeatPeriod?.split("T")[0] || "",
              scheduleId: schedule.id,
              notificationId: schedule.notificationId,
              type: schedule.type,
              video: schedule.video,
              name: schedule.name,
              isScheduleEdit: true,
            };

            setEditingData(dialogData);
            setIsEditing(true);
            setIsDialogOpen(true);
            return;
          } else {
            console.warn(
              "[SchedulerDashboardMobile] Temp event not found in cache:",
              eventId
            );
            return;
          }
        }

        // Load real event from fetched schedules
        const schedule = schedules.find(
          (s) => Number(s.id) === Number(eventId)
        );

        if (!schedule) {
          console.warn(
            "[SchedulerDashboardMobile] Could not find schedule for event:",
            eventId
          );
          return;
        }

        // Convert notificationTime to local time with proper date extraction
        const localDate = new Date(schedule.notificationTime);
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, "0");
        const day = String(localDate.getDate()).padStart(2, "0");
        const date = `${year}-${month}-${day}`;
        const time = localDate.toTimeString().slice(0, 5);

        const dialogData = {
          date: date || "",
          time: time || "",
          exerciseTypes: [schedule.preferenceId],
          repeatInterval: schedule.repeatInterval || "NONE",
          repeatPeriod: schedule.repeatPeriod?.split("T")[0] || "",
          scheduleId: schedule.id,
          notificationId: schedule.notificationId,
          type: schedule.type,
          video: schedule.video,
          name: schedule.name,
          isScheduleEdit: true,
        };

        setEditingData(dialogData);
        setIsEditing(true);
        setIsDialogOpen(true);
      },
      [schedules, queryClient, email, timezoneOffset, currentLanguage]
    );

    // Handle calendar date selection
    const handleDateClick = useCallback(
      (info) => {
        const selectedEventDate = info.dateStr;
        const currentTime = new Date().toTimeString().slice(0, 5);

        const defaultData = {
          date: selectedEventDate,
          time: currentTime,
          exerciseTypes: exerciseTypes.length > 0 ? [exerciseTypes[0].id] : [],
          repeatInterval: "NONE",
          repeatPeriod: "",
          name: "", // Empty for new entries
          video: null,
          type: null,
          isScheduleEdit: false,
        };

        setEditingData(defaultData);
        setIsEditing(false);
        setIsDialogOpen(true);
      },
      [exerciseTypes]
    );

    // Add to Calendar functionality
    const handleAddToCalendarClicked = useCallback(async () => {
      const currentHomeData = queryClient.getQueryData([
        "homeData",
        email,
        timezoneOffset,
        currentLanguage,
      ]);

      const userId = currentHomeData?.rawHomeData?.id;

      if (!userId) {
        console.warn("User ID not available for calendar export");
        return;
      }

      const token = await getCalendarToken();
      const config = {
        name: "Dynamic Desk Calendar",
        iCalFileName: "Dynamic Desk Calendar",
        startDate: "2023-10-01",
        icsFile: `${process.env.REACT_APP_FIREBASE_API_HOST}/api/ical/${token}`,
        subscribe: true,
        options: [
          "Apple",
          "Google",
          "iCal",
          "Outlook.com",
          "Microsoft365",
          "MicrosoftTeams",
          "Yahoo",
        ],
      };
      atcb_action(config);
    }, [queryClient, email, timezoneOffset, currentLanguage, getCalendarToken]);

    // Loading state
    const isLoading = useMemo(() => {
      return schedulerIsFetching || homeIsLoading;
    }, [schedulerIsFetching, homeIsLoading]);

    return (
      <Container
        maxWidth="sm"
        sx={{ height: "100vh", overflow: "hidden", padding: "0 3px" }}
      >
        {/* Welcome greeting */}
        <div style={{ marginLeft: "0.75rem" }}>
          <Typography variant="h6" sx={{ color: "#FFFFFF", padding: "0px 0" }}>
            {t("Welcome, {{name}}", { name: userName })}
          </Typography>
        </div>

        {/* Calendar container */}
        <div
          ref={calendarContainerRef}
          style={{
            marginTop: "0.75rem",
            width: "100%",
            height: "90%",
            justifyContent: "center",
            alignContent: "center",
            overflowY: "scroll",
          }}
          className="white-background-mobile"
        >
          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error display */}
          {schedulerError && (
            <Box sx={{ bgcolor: "#ffebee", p: 2, borderRadius: 1, mb: 2 }}>
              <Typography color="error">{t("errorOccurred")}</Typography>
              <Button size="small" onClick={() => refreshData()} sx={{ mt: 1 }}>
                {t("retry")}
              </Button>
            </Box>
          )}

          <div style={{ width: "100%" }} className="clickableCursor">
            <FullCalendar
              ref={calendarRef}
              plugins={[listPlugin, interactionPlugin]}
              initialView="listWeek"
              events={calendarEvents}
              headerToolbar={{ start: "", center: "title", end: "" }}
              footerToolbar={{ start: "prev", center: "today", end: "next" }}
              contentHeight="480px"
              locale={currentLanguage}
              buttonText={{ today: t("today") }}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              firstDay={1}
            />
          </div>

          {/* Action buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "1rem",
              marginBottom: "0.5rem",
              padding: "0 16px",
            }}
          >
            <TbCalendarPlus
              className="add-entry-button"
              onClick={handleDialogOpen}
              style={{
                fontSize: "35px",
                marginLeft: "24px",
                marginRight: "24px",
                cursor: "pointer",
              }}
            />
            <TbCalendarShare
              className="add-to-calendar-button"
              onClick={handleAddToCalendarClicked}
              style={{
                fontSize: "35px",
                marginLeft: "24px",
                marginRight: "24px",
                cursor: "pointer",
              }}
            />
          </div>

          {/* Notification banner
          {showNotificationBanner && (
            <div
              className="notification-banner"
              style={{ bottom: "0px", top: "unset" }}
            >
              <span>
                {isPermissionDenied
                  ? t("notificationsBlocked")
                  : t("appNeedsPermission")}
              </span>
              {!isPermissionDenied && (
                <button
                  className="notification-banner-link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleEnablePushNotifications(e);
                  }}
                >
                  {t("enablePushNotifications")}
                </button>
              )}
              <button
                className="notification-banner-link"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenNotificationGuide(e);
                }}
              >
                {t("pusnNotificationGuide")}
              </button>
            </div>
          )} */}

          {/* Exercise entry dialog */}
          <NotificationDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            onSubmit={handleFormSubmit}
            onDelete={handleDeleteSchedule}
            exerciseTypes={exerciseTypes}
            initialData={editingData}
            isEditing={isEditing}
            loading={homeIsLoading}
            isPhysioUser={isPhysioUser}
          />

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
          >
            <Toast.Body>{snackbarMessage}</Toast.Body>
          </Snackbar>
        </div>
      </Container>
    );
  }
);

// Display name for debugging
SchedulerDashboardMobile.displayName = "SchedulerDashboardMobile";

export default SchedulerDashboardMobile;
